import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import moment from 'moment-jalaali';
import JalaliDatePicker from '../components/common/JalaliDatePicker';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

// Services and shared utils
import userActivityService, { LoginHistoryItem, LoginStats } from '../services/userActivityService';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';

// Date formatter: jYYYY/jMM/jDD HH:mm with Persian digits
const formatDate = (date?: string): string => {
  if (!date) return '-';
  return toPersianDigits(moment(date).format('jYYYY/jMM/jDD HH:mm'));
};

const formatStatusLabel = (status: string) => {
  switch (status) {
    case 'success': return 'موفق';
    case 'failed': return 'ناموفق';
    default: return status;
  }
};

const UserActivityPage: React.FC = () => {
  // Data
  const [history, setHistory] = useState<LoginHistoryItem[]>([]);
  const [stats, setStats] = useState<LoginStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'' | 'success' | 'failed'>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Delete loading
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<LoginHistoryItem | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setNetworkError(null);
    try {
      const [historyData, statsData] = await Promise.all([
        userActivityService.getAllLoginHistory(page, rowsPerPage, {
          status: filterStatus || undefined,
          userId: searchTerm || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined
        }),
        userActivityService.getLoginStats()
      ]);
      setHistory(historyData.data || []);
      setTotalItems(historyData.pagination?.total || 0);
      setStats(statsData);
    } catch (err) {
      const msg = err instanceof Error ? `خطا در ارتباط با سرور: ${err.message}` : 'خطا در بارگذاری اطلاعات';
      setNetworkError(msg);
      setHistory([]);
      setTotalItems(0);
      toast.error('خطا در بارگذاری فعالیت کاربران');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filterStatus, fromDate, toDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setFilterStatus('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const handleOpenDetailsDialog = (item: LoginHistoryItem) => {
    setCurrentItem(item);
    setDetailsDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setDetailsDialogOpen(false);
    setCurrentItem(null);
  };

  const handleDeleteOldLogs = async () => {
    if (!window.confirm('آیا از حذف لاگ‌های قدیمی‌تر از 90 روز اطمینان دارید؟')) return;
    setDeleteLoading(true);
    try {
      const result = await userActivityService.deleteLoginHistory(90);
      toast.success(`${toPersianDigits(String(result.deleted))} رکورد حذف شد`);
      loadData();
    } catch (err) {
      toast.error('خطا در حذف لاگ‌ها');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderStatusTag = (status: string) => {
    if (status === 'success') {
      return <span className="tag tag-sm tag-active">موفق</span>;
    }
    if (status === 'failed') {
      return <span className="tag tag-sm tag-inactive">ناموفق</span>;
    }
    return <span className="tag tag-sm">{status || 'نامشخص'}</span>;
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalItems);
  const hasActiveFilters = searchTerm || filterStatus || fromDate || toDate;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">فعالیت کاربران</h1>
          <p className="entity-subtitle">گزارش فعالیت و رفتار کاربران</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn"
            onClick={handleDeleteOldLogs}
            disabled={deleteLoading}
            title="حذف لاگ‌های قدیمی (بیش از 90 روز)"
            style={{ color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
          >
            {deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            <span>حذف قدیمی‌ها</span>
          </button>
          <button className="header-btn refresh" onClick={loadData} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><TimelineIcon /></div>
          <div>
            <p className="entity-stat-label">کل ورودها</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.totalLogins ?? 0))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">موفق</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.successfulLogins ?? 0))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><CancelIcon /></div>
          <div>
            <p className="entity-stat-label">ناموفق</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.failedLogins ?? 0))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon purple"><PeopleIcon /></div>
          <div>
            <p className="entity-stat-label">کاربران یکتا</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.uniqueUsers ?? 0))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="شناسه کاربر..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as any); setPage(1); }}
            >
              <option value="">همه</option>
              <option value="success">موفق</option>
              <option value="failed">ناموفق</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">از تاریخ</label>
            <JalaliDatePicker
              value={fromDate}
              onChange={(v) => { setFromDate(v); setPage(1); }}
              placeholder="تاریخ شروع"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">تا تاریخ</label>
            <JalaliDatePicker
              value={toDate}
              onChange={(v) => { setToDate(v); setPage(1); }}
              placeholder="تاریخ پایان"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                کاربر: {searchTerm.length > 10 ? searchTerm.substring(0, 8) + '...' : searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {formatStatusLabel(filterStatus)}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            {fromDate && (
              <span className="filter-tag">
                از: {toPersianDigits(moment(fromDate).format('jYYYY/jMM/jDD'))}
                <button onClick={() => setFromDate('')}>×</button>
              </span>
            )}
            {toDate && (
              <span className="filter-tag">
                تا: {toPersianDigits(moment(toDate).format('jYYYY/jMM/jDD'))}
                <button onClick={() => setToDate('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading">
            <CircularProgress size={32} />
          </div>
        ) : networkError ? (
          <div className="entity-empty">
            <div className="entity-empty-icon" style={{ color: '#dc3545' }}>⚠️</div>
            <p className="entity-empty-text">{networkError}</p>
            <button className="header-btn primary" onClick={loadData} style={{ marginTop: 16 }}>
              تلاش مجدد
            </button>
          </div>
        ) : history.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><TimelineIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">رکوردی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>کاربر</th>
                  <th>وضعیت</th>
                  <th>IP</th>
                  <th>مرورگر</th>
                  <th>دستگاه</th>
                  <th>تاریخ</th>
                  <th style={{ width: 80 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          <PersonIcon />
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">
                            {item.userId?.name || item.userId?.email || 'نامشخص'}
                          </span>
                          {item.userId?.email && item.userId?.name && (
                            <span className="entity-desc">{item.userId.email}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {renderStatusTag(item.status)}
                      {item.failReason && (
                        <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 2 }}>
                          {item.failReason}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="cell-code" style={{ direction: 'ltr' }}>
                        {toPersianDigits(item.ip || '-')}
                      </span>
                    </td>
                    <td>{item.browser || '-'}</td>
                    <td>{item.device || '-'}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => handleOpenDetailsDialog(item)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {history.map((item) => (
                <div key={item._id} className={`entity-card ${item.status === 'failed' ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      <PersonIcon />
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">
                        {item.userId?.name || item.userId?.email || 'نامشخص'}
                      </div>
                      <div className="entity-card-desc">
                        {formatDate(item.createdAt)}
                      </div>
                    </div>
                    <div className="entity-card-status">
                      {renderStatusTag(item.status)}
                    </div>
                  </div>
                  <div style={{ padding: '8px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
                    <span className="tag tag-sm tag-platform" style={{ direction: 'ltr' }}>
                      IP: {toPersianDigits(item.ip || '-')}
                    </span>
                    {item.browser && <span className="tag tag-sm tag-platform">{item.browser}</span>}
                    {item.device && <span className="tag tag-sm tag-platform">{item.device}</span>}
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn" onClick={() => handleOpenDetailsDialog(item)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalItems))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>
                  {toPersianDigits(String(page))} / {toPersianDigits(String(totalPages || 1))}
                </span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
        className="activity-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات فعالیت کاربر</span>
          <button className="details-close" onClick={handleCloseDialogs}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {currentItem && (
            <div className="edit-form">
              {/* Summary Card */}
              <div className="edit-card">
                <div className="edit-card-header">
                  خلاصه ورود
                  <span className="edit-card-badge" style={
                    currentItem.status === 'success'
                      ? { background: '#dcfce7', color: '#166534' }
                      : { background: '#fee2e2', color: '#991b1b' }
                  }>
                    {formatStatusLabel(currentItem.status)}
                  </span>
                </div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام کاربر</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                        {currentItem.userId?.name || 'نامشخص'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                        {currentItem.userId?.email || '-'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>شناسه کاربر</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 12, direction: 'ltr', textAlign: 'right', fontFamily: 'monospace' }}>
                        {currentItem.userId?._id || '-'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ و زمان</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                        {formatDate(currentItem.createdAt)}
                      </div>
                    </div>
                  </div>
                  {currentItem.failReason && (
                    <div style={{ marginTop: 12, padding: 12, background: '#fee2e2', borderRadius: 6, borderRight: '3px solid #dc2626' }}>
                      <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, marginBottom: 4 }}>دلیل ناموفق بودن</div>
                      <div style={{ fontSize: 13, color: '#7f1d1d' }}>{currentItem.failReason}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Connection Card */}
              <div className="edit-card">
                <div className="edit-card-header">
                  <PublicIcon fontSize="small" />
                  اطلاعات اتصال
                </div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>IP Address</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right', fontFamily: 'monospace' }}>
                        {toPersianDigits(currentItem.ip || '-')}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>سیستم عامل</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                        {currentItem.os || '-'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>مرورگر</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                        {currentItem.browser || '-'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>دستگاه</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                        {currentItem.device || '-'}
                      </div>
                    </div>
                    {currentItem.userAgent && (
                      <div className="edit-field full">
                        <label>User Agent</label>
                        <div style={{ padding: '10px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 12, direction: 'ltr', textAlign: 'right', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {currentItem.userAgent}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn submit" onClick={handleCloseDialogs}>بستن</button>
        </div>
      </Dialog>
    </div>
  );
};

export default UserActivityPage;
